import { useEffect, useRef, RefObject } from 'react';

// Same thresholds as CameraSetupStep and useCameraObstructionDetection
const DARK_THRESHOLD = 30;
const BRIGHT_THRESHOLD = 240;
const VARIANCE_THRESHOLD = 100;
const CONSECUTIVE_BLOCKED_FRAMES = 10; // Confirm obstruction before notifying (post-verification)

interface UseWizardCameraObstructionOptions {
  streamRef: RefObject<MediaStream | null>;
  enabled: boolean;
  onObstructed: () => void;
  checkIntervalMs?: number;
}

/**
 * Runs camera obstruction detection in the background during the device setup wizard.
 * Use when the user has left the camera step (microphone/screen share) so we can
 * detect if they physically block the camera and send them back to re-verify.
 */
export function useWizardCameraObstruction({
  streamRef,
  enabled,
  onObstructed,
  checkIntervalMs = 200,
}: UseWizardCameraObstructionOptions): void {
  const blockedFrameCountRef = useRef(0);
  const onObstructedRef = useRef(onObstructed);
  onObstructedRef.current = onObstructed;

  useEffect(() => {
    if (!enabled || !streamRef.current) {
      blockedFrameCountRef.current = 0;
      return;
    }

    const stream = streamRef.current;
    const video = document.createElement('video');
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.style.position = 'absolute';
    video.style.opacity = '0';
    video.style.pointerEvents = 'none';
    video.style.width = '1px';
    video.style.height = '1px';
    video.style.left = '-9999px';
    video.srcObject = stream;
    video.play().catch(() => {});

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      return () => {
        video.srcObject = null;
      };
    }

    const sampleWidth = 160;
    const sampleHeight = 120;
    canvas.width = sampleWidth;
    canvas.height = sampleHeight;

    const intervalId = setInterval(() => {
      if (video.readyState < video.HAVE_CURRENT_DATA || video.videoWidth === 0) return;

      try {
        ctx.drawImage(video, 0, 0, sampleWidth, sampleHeight);
        const imageData = ctx.getImageData(0, 0, sampleWidth, sampleHeight);
        const pixels = imageData.data;

        let totalBrightness = 0;
        const brightnessValues: number[] = [];
        const pixelCount = pixels.length / 4;

        for (let i = 0; i < pixels.length; i += 4) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
          totalBrightness += brightness;
          brightnessValues.push(brightness);
        }

        const avgBrightness = totalBrightness / pixelCount;
        let varianceSum = 0;
        for (const b of brightnessValues) {
          varianceSum += Math.pow(b - avgBrightness, 2);
        }
        const variance = varianceSum / pixelCount;

        let detectedReason: string | null = null;
        if (avgBrightness < DARK_THRESHOLD) {
          detectedReason = 'dark';
        } else if (avgBrightness > BRIGHT_THRESHOLD && variance < VARIANCE_THRESHOLD) {
          detectedReason = 'bright';
        } else if (variance < VARIANCE_THRESHOLD) {
          detectedReason = 'uniform';
        }

        if (detectedReason) {
          blockedFrameCountRef.current++;
          if (blockedFrameCountRef.current >= CONSECUTIVE_BLOCKED_FRAMES) {
            onObstructedRef.current();
          }
        } else {
          blockedFrameCountRef.current = 0;
        }
      } catch {
        // Ignore frame read errors
      }
    }, checkIntervalMs);

    return () => {
      clearInterval(intervalId);
      video.srcObject = null;
    };
  }, [enabled, streamRef, checkIntervalMs]);
}
